import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './doneEventsTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceDoneEventsTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import DoneEventsTableRow from './DoneEventsTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function DoneEventsTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { doneEvents } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'EventType'),
      field: TABLE_SORT_FIELDS.EVENT_TYPE,
    },
    {
      id: 3,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 4,
      title: Trans(translations, 'Agent'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 5,
      title: Trans(translations, 'EventQualification'),
      field: TABLE_SORT_FIELDS.QUALIFICATION,
    },
    {
      id: 6,
      title: Trans(translations, 'EventDate'),
      field: TABLE_SORT_FIELDS.WHEN,
    },
  ];

  useEffect(() => {
    if (!doneEvents.has_next) return;
    if (!isInViewport || doneEvents.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(doneEvents, {
      withNextPage: true,
    });

    dispatch(getPerformanceDoneEventsTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'doneEvents',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.doneEvents(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="done-events-table"
      className={`${parentStyles.table} ${specificStyles.doneEventsTableWithQualification}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={doneEvents.sortBy}
            sortDirection={doneEvents.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {doneEvents.list.map((doneEvent) => (
          <DoneEventsTableRow
            key={doneEvent.id}
            doneEvent={doneEvent}
            showQualificationColumn
          />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(doneEvents.loading || doneEvents.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
